import { useCallback, useEffect, useRef } from 'react';

/**
 * § 303 — the ONE debounce for the design system's async fields.
 *
 * Measured 2026-09-15: 32 of 43 `Search` consumers and 11 of 23 `onSearch` /
 * `onSearchChange` consumers hand-rolled a debounce or a `setTimeout` around a field
 * that already knew it was being typed into — at 300, 600 and 700 ms depending on the
 * module, each copy carrying its own cleanup (or not: the ListEdit latent debounce bug
 * is a copy that fired after unmount). A field that reports a query while the user
 * types is the design system's to pace; the caller's job is what to do with the query.
 *
 * `DEBOUNCE.search` is the rung. Pass it — not a literal — so the app types at one
 * speed and a retune is one number.
 */
export const DEBOUNCE = {
  /** A search field reporting its query while the user types. */
  search: 300,
} as const;

export interface DebouncedCallback<A extends unknown[]> {
  /** Schedule the callback with these arguments — or, with no delay set, fire it now. */
  call: (...args: A) => void;
  /** Cancel anything pending and fire NOW — Enter, a clear ×, a reset. */
  now: (...args: A) => void;
  /** Drop anything pending without firing. */
  cancel: () => void;
}

/**
 * A debounced wrapper around an optional callback.
 *
 * - `fn` is read through a ref, so a caller may pass an inline closure without the
 *   timer restarting on every render.
 * - `ms` undefined or 0 means NO debounce: `call` fires synchronously, which is what
 *   keeps every field byte-identical in behaviour until a caller opts in.
 * - Anything pending is cancelled on unmount — the class of bug this replaces.
 */
export function useDebouncedCallback<A extends unknown[]>(
  fn: ((...args: A) => void) | undefined,
  ms: number | undefined,
): DebouncedCallback<A> {
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancel = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const now = useCallback((...args: A) => {
    cancel();
    fnRef.current?.(...args);
  }, [cancel]);

  const call = useCallback((...args: A) => {
    if (!ms) {
      fnRef.current?.(...args);
      return;
    }
    cancel();
    timer.current = setTimeout(() => {
      timer.current = null;
      fnRef.current?.(...args);
    }, ms);
  }, [ms, cancel]);

  useEffect(() => cancel, [cancel]);

  return { call, now, cancel };
}
